import { useEffect, useState } from 'react';
import { 
  notificationService, 
  Notification, 
  NotificationType 
} from '../services/NotificationService';
import { NotificationSettings } from '../types';
import { 
  feedbackService, 
  FeedbackType, 
  FeedbackOptions,
  VisualFeedbackState 
} from '../services/FeedbackService';

export interface UseNotificationsReturn {
  // Notification state
  notifications: Notification[];
  notificationSettings: NotificationSettings;
  
  // Notification actions
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => string;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  clearNotificationsByType: (type: NotificationType) => void;
  updateNotificationSettings: (settings: Partial<NotificationSettings>) => void;
  
  // Convenience notification methods
  showTurnNotification: (gameId: string, playerName: string) => void;
  showGameInvite: (fromPlayer: string, gameId: string) => void;
  showGameResult: (result: 'win' | 'loss' | 'draw', opponent?: string) => void;
  
  // Feedback state
  recentFeedback: VisualFeedbackState[];
  feedbackSettings: { soundEnabled: boolean; hapticEnabled: boolean };
  
  // Feedback actions
  showFeedback: (type: FeedbackType, message: string, options?: FeedbackOptions) => void;
  showMoveValidFeedback: (element?: HTMLElement) => void;
  showMoveInvalidFeedback: (element?: HTMLElement, reason?: string) => void;
  showConnectionFeedback: (connected: boolean) => void;
  showSuccess: (message: string, options?: FeedbackOptions) => void;
  showError: (message: string, options?: FeedbackOptions) => void;
  showWarning: (message: string, options?: FeedbackOptions) => void;
  showInfo: (message: string, options?: FeedbackOptions) => void;
  updateFeedbackSettings: (settings: { soundEnabled?: boolean; hapticEnabled?: boolean }) => void;
}

export const useNotifications = (): UseNotificationsReturn => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    turnBased: true,
    gameInvites: true,
    gameResults: true
  });
  const [recentFeedback, setRecentFeedback] = useState<VisualFeedbackState[]>([]);
  const [feedbackSettings, setFeedbackSettings] = useState({
    soundEnabled: true,
    hapticEnabled: true
  });

  useEffect(() => {
    // Subscribe to notification changes
    const unsubscribeNotifications = notificationService.subscribe((newNotifications) => {
      setNotifications(newNotifications);
    });

    // Subscribe to feedback changes
    const unsubscribeFeedback = feedbackService.subscribe((feedback) => {
      setRecentFeedback(prev => [feedback, ...prev.slice(0, 9)]); // Keep last 10 feedback items
    });

    // Load initial state
    setNotifications(notificationService.getNotifications());
    setNotificationSettings(notificationService.getSettings());
    setRecentFeedback(feedbackService.getRecentFeedback());
    setFeedbackSettings(feedbackService.getSettings());

    return () => {
      unsubscribeNotifications();
      unsubscribeFeedback();
    };
  }, []);

  // Notification methods
  const addNotification = (notification: Omit<Notification, 'id' | 'timestamp'>) => {
    return notificationService.addNotification(notification);
  };

  const removeNotification = (id: string) => {
    notificationService.removeNotification(id);
  };

  const clearAllNotifications = () => {
    notificationService.clearAll();
  };

  const clearNotificationsByType = (type: NotificationType) => {
    notificationService.clearByType(type);
  };

  const updateNotificationSettings = (settings: Partial<NotificationSettings>) => {
    notificationService.updateSettings(settings);
    setNotificationSettings(notificationService.getSettings());
  };

  // Convenience notification methods
  const showTurnNotification = (gameId: string, playerName: string) => {
    notificationService.sendTurnNotification(gameId, playerName);
  };

  const showGameInvite = (fromPlayer: string, gameId: string) => {
    notificationService.sendGameInviteNotification(fromPlayer, gameId);
  };

  const showGameResult = (result: 'win' | 'loss' | 'draw', opponent?: string) => {
    notificationService.sendGameResultNotification(result, opponent);
  };

  // Feedback methods
  const showFeedback = (type: FeedbackType, message: string, options?: FeedbackOptions) => {
    feedbackService.showFeedback(type, message, options);
  };

  const showMoveValidFeedback = (element?: HTMLElement) => {
    feedbackService.showMoveValidFeedback(element);
  };

  const showMoveInvalidFeedback = (element?: HTMLElement, reason?: string) => {
    feedbackService.showMoveInvalidFeedback(element, reason);
  };

  const showConnectionFeedback = (connected: boolean) => {
    feedbackService.showConnectionFeedback(connected);
  };

  const showSuccess = (message: string, options?: FeedbackOptions) => {
    feedbackService.showSuccess(message, options);
  };

  const showError = (message: string, options?: FeedbackOptions) => {
    feedbackService.showError(message, options);
  };

  const showWarning = (message: string, options?: FeedbackOptions) => {
    feedbackService.showWarning(message, options);
  };

  const showInfo = (message: string, options?: FeedbackOptions) => {
    feedbackService.showInfo(message, options);
  };

  const updateFeedbackSettings = (settings: { soundEnabled?: boolean; hapticEnabled?: boolean }) => {
    feedbackService.updateSettings(settings);
    setFeedbackSettings(feedbackService.getSettings());
  };

  return {
    // Notification state
    notifications,
    notificationSettings,
    
    // Notification actions
    addNotification,
    removeNotification,
    clearAllNotifications,
    clearNotificationsByType,
    updateNotificationSettings,
    
    // Convenience notification methods
    showTurnNotification,
    showGameInvite,
    showGameResult,
    
    // Feedback state
    recentFeedback,
    feedbackSettings,
    
    // Feedback actions
    showFeedback,
    showMoveValidFeedback,
    showMoveInvalidFeedback,
    showConnectionFeedback,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    updateFeedbackSettings
  };
};

export default useNotifications;